/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import wasteAPI from '../../services/modules/IP/wasteAPI';
import WasteGenJSON from '../../services/modules/JSON/wasteCategory';
import wasteTypeJSON from '../../services/modules/JSON/wasteType';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import DisplayModal from '../DisplayModal/DisplayModal';
import DisposalDisplay from '../DisplayModal/WasteDisplay/DisposalDisplay';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {WasteKey, WasteLabel} from '../Forms/Constants/IP/WasteKeyLabel';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import WasteForm from '../Forms/WasteForm';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import sumValue, {sumOfTimeFrames} from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const WasteTable: React.FC<{
  selectedYear: string;
  facilityId: string;
}> = ({selectedYear, facilityId}) => {
  interface DataType {
    _id: string;
    fuelType: number;
    noOfUnits: boolean;
    deviceType: number;
    fuelUsed: boolean;
    method: number;
    impacts: boolean;
    owned: boolean;
    quantity: number;
    cost: number;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const {wasteCategory, wasteType} = useJSONStore.getState();
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [disposalModalData, setDisposalModalData] = useState([]);
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });
  const [facilityEfdb, setFacilityEfdb] = useState<any>();

  const [showModelInfo, setShowModelInfo] = useState(false);
  const {efdbData} = useContext(SelectedCountryContext);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      wasteAPI(APITypeEnum.GET_ALL, undefined, `${facilityId}/${selectedYear}`)
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([WasteGenJSON(), wasteTypeJSON()])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      wasteAPI(APITypeEnum.GET_ALL, undefined, `${facilityId}/${selectedYear}`)
        .then((res) => {
          setDataSource(res ?? []);
          setFacilityEfdb(
            efdbData?.[selectedYear]?.find((i: any) => {
              return i.facilityId === facilityId;
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  const handleDelete = (_id: string[] | string) => {
    wasteAPI(APITypeEnum.DELETE_ALL, {
      waste: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const wasteKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: WasteKey.wasteCategory,
      label: WasteLabel.wasteCategory,
      type: objTypes.json,
      format: wasteCategory
    },
    {
      key: WasteKey.wasteType,
      label: WasteLabel.wasteType,
      type: objTypes.json,
      format: wasteType
    },
    {
      key: TF,
      label: [WasteLabel.wasteProduced],
      type: objTypes.timeFrameData,
      renderType: 'multi'
    },
    {
      key: WasteKey.disposalMethodData,
      mainLabel: [
        WasteLabel.disposalKey.landfill,
        WasteLabel.disposalKey.incineration,
        WasteLabel.disposalKey.recycling,
        WasteLabel.disposalKey.reclaimed,
        WasteLabel.disposalKey.reused,
        WasteLabel.disposalKey.wasteNotTreated
      ],
      label: [
        WasteLabel.treated.wasteTreated,
        WasteLabel.treated.treatmentCost
      ],
      type: objTypes.timeFrameData,
      renderType: 'multi'
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: WasteLabel.wasteCategory,
        dataIndex: WasteKey.wasteCategory,
        editable: true,
        type: 'select',
        ...SearchFilter(
          WasteLabel.wasteCategory,
          WasteKey.wasteCategory,
          wasteCategory
        ),
        render: (value) => {
          return (
            <>
              {
                wasteCategory?.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: WasteLabel.wasteType,
        dataIndex: WasteKey.wasteType,
        editable: true,
        type: 'select',
        ...SearchFilter(WasteLabel.wasteType, WasteKey.wasteType, wasteType),
        render: (value) => {
          return (
            <>
              {
                wasteType?.find((item) => {
                  return (
                    item?.value === value &&
                    item?.dbName === facilityEfdb?.scope3
                  );
                })?.label
              }
            </>
          );
        }
      },
      {
        title: WasteLabel.wasteProduced,
        dataIndex: TF,
        editable: true,
        type: 'select',
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item?.type === WasteKey.wasteProduced;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${WasteLabel.wasteProduced} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: WasteKey.wasteProduced
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                mainData?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      // {
      //   title: WasteLabel.disposalMethodData,
      //   dataIndex: WasteKey.disposalMethodData,
      //   editable: true,
      //   type: 'select',
      //   render: (value) => {
      //     const filtered = value.filter((item: any) => {
      //       return !!item.disposalMethod && item.disposalMethod !== 'notTreated';
      //     });

      //     return (
      //       <a
      //         onClick={() => {
      //           setOpenDisposalModal(true);
      //           setDisposalModalData(filtered);
      //         }}
      //       >
      //         <div>{filtered?.length}</div>
      //       </a>
      //     );
      //   }
      // },
      {
        title: WasteLabel.treated.wasteTreated,
        dataIndex: WasteKey.disposalMethodData,
        editable: true,
        type: 'select',
        render: (value: any) => {
          const filtered = value?.filter((item: any) => {
            return (
              !!item.disposalMethod && item.disposalMethod !== 'notTreated'
            );
          });

          const wasteTreatedUnit = filtered?.[0]?.timeFrameData?.find(
            (item: any) => item?.type === WasteKey.treated.wasteTreated
          )?.unit;

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setDisposalModalData(filtered);
                setOpenDisplayModalType({
                  title: `${WasteLabel.treated.wasteTreated} (${
                    wasteTreatedUnit ?? ''
                  })`,
                  dataKey: WasteKey.treated.wasteTreated
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${sumOfTimeFrames(
                filtered,
                WasteKey.treated.wasteTreated
              )}`}</div>
            </div>
          );
        }
      },
      {
        title: WasteLabel.treated.treatmentCost,
        dataIndex: WasteKey.disposalMethodData,
        editable: true,
        type: 'select',
        render: (value) => {
          const filtered = value.filter((item: any) => {
            return (
              !!item.disposalMethod && item.disposalMethod !== 'notTreated'
            );
          });

          const wasteTreatmentCost = filtered?.[0]?.timeFrameData?.find(
            (item: any) => item?.type === WasteKey.treated.treatmentCost
          )?.unit;

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setDisposalModalData(filtered);
                setOpenDisplayModalType({
                  title: `${WasteLabel.treated.treatmentCost} (${wasteTreatmentCost})`,
                  dataKey: WasteKey.treated.treatmentCost
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${sumOfTimeFrames(
                filtered,
                WasteKey.treated.treatmentCost,
                true
              )}`}</div>
            </div>
          );
        }
      },
      {
        title: WasteLabel.notTreated.wasteNotTreated,
        dataIndex: WasteKey.disposalMethodData,
        editable: true,
        type: 'select',
        render: (value) => {
          const filtered = value.filter((item: any) => {
            return (
              !!item.disposalMethod && item.disposalMethod === 'notTreated'
            );
          })?.[0]?.[TF]?.[0];

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(filtered);
                setOpenDisplayModalType({
                  title: `${WasteLabel.notTreated.wasteNotTreated} (${
                    filtered?.unit ?? ''
                  })`,
                  dataKey: ''
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(filtered?.data)))} ${
                filtered?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) => {
                return dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null;
              }
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <>
      <div>
        <TableTitleHeader
          title="Waste Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Waste'
          }}
          sampleDownload={{href: SampleExcel.waste}}
          jobs={{setShowJobs, uploadType: uploadTypes.Waste}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          showAddOnly={true}
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />

        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          details={`${facilityId}/${selectedYear}`}
        />

        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item: any) => {
                        return item?.status !== statusEm.ACCEPTED
                          ? item._id
                          : null;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />
        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          API={wasteAPI}
          data={isEdit ? editData : undefined}
          FormComponent={<WasteForm />}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          stepsTitle={[
            'Configure Details',
            'Waste Details',
            'Quantity & Cost Details'
          ]}
          formPreview={wasteKeyLabel}
        />
        <DisplayModal
          title={openDisplayModalType.title}
          open={openDisplayModal}
          onCancel={() => {
            setOpenDisplayModal(false);
          }}
          displayComponent={
            [
              WasteLabel.wasteProduced,
              WasteLabel.notTreated.wasteNotTreated
            ].some((label) => openDisplayModalType.title.includes(label)) ? (
              <TimeFrameDataTooltip mainData={openDisplayModalData} />
            ) : (
              <DisposalDisplay
                disposalModalData={disposalModalData}
                disposalModalType={openDisplayModalType.dataKey}
                disposalKey={WasteKey.disposalMethod}
              />
            )
          }
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={wasteKeyLabel} />
        }
        API={wasteAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        // formPreview={wasteKeyLabel}
      />
    </>
  );
};

export default WasteTable;
